import React, { Component } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AssistantMark } from './AssistantMark';
interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}
function formatTime(date: Date): string {
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
}
/** Tasteful prose styling for assistant markdown — monochrome, on-brand. */
const markdownComponents = {
  p: ({
    children
  }: any) => <p className="text-[15px] leading-relaxed text-white/85 mb-3 last:mb-0" data-id="element-199">
      {children}
    </p>,
  strong: ({
    children
  }: any) => <strong className="font-semibold text-white" data-id="element-200">{children}</strong>,
  em: ({
    children
  }: any) => <em className="italic text-white/90" data-id="element-201">{children}</em>,
  a: ({
    children,
    href
  }: any) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-white underline underline-offset-2 decoration-white/40 hover:decoration-white transition-colors" data-id="element-202">
      {children}
    </a>,
  ul: ({
    children
  }: any) => <ul className="list-disc pl-5 mb-3 last:mb-0 space-y-1.5 marker:text-white/40" data-id="element-203">
      {children}
    </ul>,
  ol: ({
    children
  }: any) => <ol className="list-decimal pl-5 mb-3 last:mb-0 space-y-1.5 marker:text-white/40" data-id="element-204">
      {children}
    </ol>,
  li: ({
    children
  }: any) => <li className="text-[15px] leading-relaxed text-white/85" data-id="element-205">{children}</li>,
  h1: ({
    children
  }: any) => <h3 className="text-base font-semibold text-white mb-2 mt-1" data-id="element-206">{children}</h3>,
  h2: ({
    children
  }: any) => <h3 className="text-base font-semibold text-white mb-2 mt-1" data-id="element-207">{children}</h3>,
  h3: ({
    children
  }: any) => <h4 className="text-sm font-semibold text-white mb-1.5 mt-1" data-id="element-208">{children}</h4>,
  code: ({
    children
  }: any) => <code className="px-1.5 py-0.5 rounded-md bg-white/10 text-white text-[13px] font-mono" data-id="element-209">
      {children}
    </code>,
  blockquote: ({
    children
  }: any) => <blockquote className="border-l-2 border-white/20 pl-3 italic text-white/70 my-3" data-id="element-210">
      {children}
    </blockquote>
};
export function ChatMessage({
  role,
  content,
  timestamp,
  isStreaming = false
}: ChatMessageProps) {
  const isUser = role === 'user';
  if (isUser) {
    // User turn — right-aligned, contained neutral surface, plain text.
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="group flex flex-col items-end" data-id="element-211">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-white text-slate-900 px-4 py-2.5" data-id="element-212">
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap" data-id="element-213">
            {content}
          </p>
        </div>
        <span className="mt-1 mr-1 text-[11px] text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-214">
          {formatTime(timestamp)}
        </span>
      </motion.div>;
  }
  // Assistant turn — full-width, no bubble, small leading mark, markdown body.
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="group flex gap-3" data-id="element-215">
      <AssistantMark size={28} className="mt-0.5" data-id="element-216" />
      <div className="flex-1 min-w-0 pt-0.5" data-id="element-217">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} data-id="element-218">
          {content}
        </ReactMarkdown>
        {isStreaming && <motion.span animate={{
        opacity: [0, 1, 0]
      }} transition={{
        duration: 1,
        repeat: Infinity
      }} className="inline-block w-1.5 h-4 ml-0.5 bg-white align-middle rounded-sm" aria-hidden="true" data-id="element-219" />}
        <span className="mt-1 block text-[11px] text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-220">
          {formatTime(timestamp)}
        </span>
      </div>
    </motion.div>;
}